import React, { useState, useEffect } from "react";
import { GLOBAL_CONSTANTS } from "../../constants";
import Dropdown from "../../components/Dropdown";
import UnloadMethodSelect from "./UnloadMethodSelect";
import { FaChevronDown } from "react-icons/fa";
import {
  MSK_DELIVERY_CALCULATION_OPTIONS,
  getDefaultMskDeliveryMode,
  normalizeRegionCode,
} from "./new-delivery-msk";
import {
  UNLOAD_METHOD_OPTIONS,
  getUnloadQuestionsByMethod,
  hasGazobetonProduct,
  isSameDeliveryMethod,
} from "./deliveryMethodsHelpers";
import { applyGazUnloadMethod } from "./gazUnloadMethod";
import "./DeliveryMethods.css";
const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;
const MSK_DELIVERY_TRACE_LOG = "MSK_DELIVERY_TRACE";

const QUESTIONS_PANEL_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  marginTop: "12px",
  background: "linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)",
  border: "1px solid #dbeafe",
  borderRadius: "12px",
  padding: "14px 16px",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
  overflowWrap: "anywhere",
};

const QUESTIONS_HEADER_STYLE = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "10px",
};

const QUESTIONS_TITLE_STYLE = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 700,
  color: "#0f172a",
};

const QUESTION_ITEM_STYLE = {
  display: "flex",
  gap: "10px",
  alignItems: "flex-start",
  color: "#1e293b",
  marginBottom: "10px",
  lineHeight: 1.45,
};

const QUESTION_NUMBER_STYLE = {
  minWidth: "24px",
  height: "24px",
  borderRadius: "999px",
  backgroundColor: "#dbeafe",
  color: "#1d4ed8",
  fontWeight: 700,
  fontSize: "13px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: "1px",
};

const UNLOAD_BLOCK_STYLE = {
  width: "100%",
  maxWidth: "420px",
  margin: "0",
};

const GAZ_CONTROL_LABEL_STYLE = {
  display: "block",
  marginBottom: "6px",
  fontWeight: 600,
  color: "#334155",
};

const UNLOAD_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "8px",
  width: "100%",
};

const UNLOAD_HELP_SLOT_STYLE = {
  width: "24px",
  minWidth: "24px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

// hideUnloadControl: внутри МСК Газобетона выбор разгрузки живёт в отдельном блоке
// «Разгрузка на объекте» между товарами и расчётом, поэтому здесь он не рисуется.
// Компонент при этом остаётся смонтированным — он подставляет значения по умолчанию
// и грузит справочники, от которых зависит расчёт.
function DeliveryMethods({ deliveryMethod, setDeliveryMethod, selectedRegion, products, forceExpanded = false, hideUnloadControl = false }) {
  const [collapsed, setCollapsed] = useState(true);
  // В редизайне колонка «Способ доставки» всегда раскрыта — подсказка и
  // внутренний тогглер не нужны, лейбл колонки выводится снаружи.
  const effectiveCollapsed = forceExpanded ? false : collapsed;
  const [deliveryOptions, setDeliveryOptions] = useState({});
  const [selectedUnloadMethod, setSelectedUnloadMethod] = useState("");
  const [selectedMskDeliveryMode, setSelectedMskDeliveryMode] = useState("");
  const [isQuestionsVisible, setIsQuestionsVisible] = useState(false);
  const isGazobetonSelected = hasGazobetonProduct(products, selectedRegion);
  const isMoscowRegion = normalizeRegionCode(selectedRegion) === "МСК";

  // Функция для получения уникальных категорий из выбранных товаров
  const getUsedCategories = () => {
    if (!products || products.length === 0) {
      return Object.keys(GLOBAL_CONSTANTS.CATEGORY_NAMES);
    }

    const categoryMap = new Map(
      Object.entries(GLOBAL_CONSTANTS.CATEGORY_NAMES)
        .map(([key, value]) => [value.trim().toLowerCase(), key])
    );

    const usedCategories = new Set();
    products.forEach(product => {
      // Используем product.category вместо product.calculator
      if (product.category && product.category !== "-") {
        const categoryKey = categoryMap.get(product.category.trim().toLowerCase()) || "other";
        if (categoryKey !== "other") {
          usedCategories.add(categoryKey);
        }
      }
    });

    console.log("🔍 Анализ товаров:", products.map(p => ({ name: p.name, category: p.category })));
    console.log("🔍 categoryMap:", Array.from(categoryMap.entries()));

    // Если нет определенных категорий, возвращаем все кроме нерудки
    const allCategoriesExceptNer = Object.keys(GLOBAL_CONSTANTS.CATEGORY_NAMES).filter(key => key !== "ner");
    const result = usedCategories.size > 0 ? Array.from(usedCategories) : allCategoriesExceptNer;

    // Исключаем нерудку из любого результата, так как она обрабатывается отдельно
    const finalResult = result.filter(category => category !== "ner");
    console.log("🚚 Категории для DeliveryMethods (без нерудки):", finalResult);
    return finalResult;
  };

  useEffect(() => {
    if (!Array.isArray(products)) {
      return;
    }

    // Игнорируем временные изменения (с флагом _isTemporary)
    const hasTemporaryChanges = products.some(product => product._isTemporary);
    if (hasTemporaryChanges) {
      return;
    }

    console.log("🚚 DeliveryMethods useEffect срабатывает, selectedRegion:", selectedRegion, "тип:", typeof selectedRegion);
    console.log("🚚 Products:", products);

    if (!selectedRegion) {
      console.log("🚚 selectedRegion пустой, прерываем выполнение");
      return;
    }

    const usedCategories = getUsedCategories();
    console.log("🚚 Используемые категории товаров:", usedCategories);

    fetch(`${PATH_TO_BACKEND}/api/deliveryMethods?region=${selectedRegion}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // Фильтруем deliveryOptions только по категориям, которые есть в товарах
          const filteredDeliveryOptions = {};
          usedCategories.forEach((category) => {
            if (data.data[category]) {
              filteredDeliveryOptions[category] = data.data[category];
            }
            console.log(selectedRegion, "00000000000000000 ЭТОТ РЕГИОН ИСПОЛЬЗУЕТСЯ 000000000000000000")
          });

          const updatedDeliveryMethod = {};
          Object.keys(filteredDeliveryOptions).forEach((category) => {
            updatedDeliveryMethod[category] = "Оптимальный маршрут";
          });

          console.log("🚚 Отфильтрованные способы доставки:", filteredDeliveryOptions);
          setDeliveryMethod((prev) => {
            const next = {
              ...updatedDeliveryMethod,
              ...(isGazobetonSelected ? { gaz: "Оптимальный маршрут" } : {}),
              ...(prev?.gazUnloadMethod ? { gazUnloadMethod: prev.gazUnloadMethod } : {}),
              mskDeliveryMode: prev?.mskDeliveryMode || getDefaultMskDeliveryMode(selectedRegion),
            };

            // Состав способов доставки тот же — отдаём прежний объект. Этот
            // запрос повторяется на каждое изменение «Товаров», включая добавление
            // пустой строки; новый объект с тем же содержимым App принимал за
            // смену способа доставки и снимал готовый расчёт с экрана.
            return isSameDeliveryMethod(prev, next) ? prev : next;
          });
          setDeliveryOptions(filteredDeliveryOptions);

          // Автоматически раскрываем DeliveryMethods если есть отфильтрованные категории и товары выбраны
          if (Object.keys(filteredDeliveryOptions).length > 0 && products && products.length > 0 && products.some(p => p.name && p.name !== "")) {
            setCollapsed(false);
          }
        }
      })
      .catch((error) =>
        console.error("Ошибка при загрузке данных доставки:", error)
      );
  }, [setDeliveryMethod, selectedRegion, products, isGazobetonSelected]); // Добавляем products в зависимости

  useEffect(() => {
    if (!isGazobetonSelected) {
      setSelectedUnloadMethod("");
      setIsQuestionsVisible(false);
      setDeliveryMethod((prev) => {
        if (!prev?.gazUnloadMethod && !prev?.gaz) {
          return prev;
        }

        const next = { ...prev };
        delete next.gazUnloadMethod;
        delete next.gaz;
        return next;
      });
    }
  }, [isGazobetonSelected, setDeliveryMethod]);

  useEffect(() => {
    const currentMskDeliveryMode = deliveryMethod?.mskDeliveryMode;
    const defaultMskDeliveryMode = currentMskDeliveryMode || getDefaultMskDeliveryMode(selectedRegion);

    if (selectedMskDeliveryMode !== defaultMskDeliveryMode) {
      setSelectedMskDeliveryMode(defaultMskDeliveryMode);
    }

    if (currentMskDeliveryMode !== defaultMskDeliveryMode) {
      setDeliveryMethod((prev) => ({
        ...prev,
        mskDeliveryMode: defaultMskDeliveryMode,
      }));
    }
  }, [
    deliveryMethod?.mskDeliveryMode,
    selectedMskDeliveryMode,
    selectedRegion,
    setDeliveryMethod,
  ]);

  useEffect(() => {
    if (!isGazobetonSelected) {
      return;
    }

    if (!selectedUnloadMethod) {
      return;
    }

    setIsQuestionsVisible(true);
  }, [isGazobetonSelected, selectedUnloadMethod]);

  useEffect(() => {
    if (!isGazobetonSelected) {
      return;
    }

    const currentUnloadMethod = deliveryMethod?.gazUnloadMethod;
    const defaultUnloadMethod = currentUnloadMethod || "без разгрузки";
    const currentGazDeliveryMethod = deliveryMethod?.gaz;

    if (selectedUnloadMethod !== defaultUnloadMethod) {
      setSelectedUnloadMethod(defaultUnloadMethod);
    }

    if (
      !currentUnloadMethod ||
      currentGazDeliveryMethod !== "Оптимальный маршрут"
    ) {
      setDeliveryMethod((prev) => ({
        ...prev,
        gaz: "Оптимальный маршрут",
        gazUnloadMethod: defaultUnloadMethod,
      }));
    }
  }, [
    deliveryMethod?.gaz,
    deliveryMethod?.gazUnloadMethod,
    isGazobetonSelected,
    selectedUnloadMethod,
    setDeliveryMethod,
  ]);

  const handleDeliveryChange = (key, value) => {
    setDeliveryMethod((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleUnloadMethodChange = (event) => {
    const unloadMethod = event.target.value;
    setSelectedUnloadMethod(unloadMethod);
    setIsQuestionsVisible(false);
    // Запись идёт через общий обработчик: то же поле общего состояния правит и
    // переключатель в разделе «Товары».
    applyGazUnloadMethod(setDeliveryMethod, unloadMethod);
  };

  const handleMskDeliveryModeChange = (event) => {
    const mode = event.target.value;
    console.log(MSK_DELIVERY_TRACE_LOG, {
      layer: "client",
      source: "DeliveryMethods",
      event: "mode_change",
      selectedRegion,
      mode,
      products: Array.isArray(products) ? products.map((product) => ({
        name: product?.name || null,
        category: product?.category || null,
        source: product?.source || null,
      })) : [],
    });
    setSelectedMskDeliveryMode(mode);
    setDeliveryMethod((prev) => ({
      ...prev,
      mskDeliveryMode: mode,
    }));
  };

  const toggleCollapse = () => {
    setCollapsed((prev) => !prev);
  };

  const isAllOptimal = Object.keys(deliveryOptions).every(
    (key) => deliveryMethod?.[key] === "Оптимальный маршрут"
  );

  return (
    <div className="d-flex flex-column gap-3 delivery-methods__root">
      {!forceExpanded && (
        <div className="delivery-methods__header">
          {isGazobetonSelected ? (
            <span className="delivery-methods__title">Доставка газобетона</span>
          ) : (
            <button
              type="button"
              className="delivery-methods__toggle"
              onClick={toggleCollapse}
              aria-expanded={!collapsed}
              aria-label={!collapsed ? "Свернуть способы доставки" : "Развернуть способы доставки"}
            >
              <span className="delivery-methods__title">Способы доставки</span>
              <FaChevronDown
                className={`delivery-methods__chevron ${!collapsed ? "is-open" : ""}`}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      )}
      {!isGazobetonSelected && !forceExpanded && collapsed && (
        <p>
          {isAllOptimal
            ? "*выбран оптимальный способ доставки"
            : "*способ доставки указан вручную"}
        </p>
      )}
      {/* {isMoscowRegion && !isGazobetonSelected && (
        <div style={UNLOAD_BLOCK_STYLE}>
          <label style={GAZ_CONTROL_LABEL_STYLE}>Тип расчета доставки</label>
          <Dropdown
            value={selectedMskDeliveryMode}
            onChange={handleMskDeliveryModeChange}
            align="left"
            maxWidth="320px"
            className="delivery-methods__select-wrap"
            selectClassName="delivery-methods__select"
          >
            {MSK_DELIVERY_CALCULATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Dropdown>
        </div>
      )} */}
      {isGazobetonSelected && !hideUnloadControl && (
        <>
          <div style={forceExpanded ? undefined : UNLOAD_BLOCK_STYLE} className="delivery-methods__gaz-block">
            {!forceExpanded && (
              <label style={GAZ_CONTROL_LABEL_STYLE}>Способ разгрузки</label>
            )}
            <div style={UNLOAD_ROW_STYLE}>
              {forceExpanded ? (
                <UnloadMethodSelect
                  value={selectedUnloadMethod}
                  options={UNLOAD_METHOD_OPTIONS}
                  onChange={handleUnloadMethodChange}
                />
              ) : (
                <Dropdown
                  value={selectedUnloadMethod}
                  onChange={handleUnloadMethodChange}
                  align="left"
                  maxWidth="320px"
                  className="delivery-methods__select-wrap"
                  selectClassName="delivery-methods__select"
                >
                  {UNLOAD_METHOD_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Dropdown>
              )}
              {!forceExpanded && (
                <div style={UNLOAD_HELP_SLOT_STYLE}>
                  {selectedUnloadMethod && (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      aria-label="Показать вопросы для клиента"
                      onClick={() => setIsQuestionsVisible((prev) => !prev)}
                      style={{ lineHeight: 1 }}
                    >
                      <i className="bi bi-question-circle-fill fs-5"></i>
                    </button>
                  )}
                </div>
              )}
            </div>
            {!forceExpanded && isQuestionsVisible && selectedUnloadMethod && (
              <div style={QUESTIONS_PANEL_STYLE}>
                <div style={QUESTIONS_HEADER_STYLE}>
                  <p style={QUESTIONS_TITLE_STYLE}>
                    Подсказка
                  </p>
                </div>
                <div>
                  {getUnloadQuestionsByMethod(selectedUnloadMethod).map((question, index) => (
                    <div key={question} style={QUESTION_ITEM_STYLE}>
                      <span style={QUESTION_NUMBER_STYLE}>{index + 1}</span>
                      <span>{question}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {!isGazobetonSelected &&
        !effectiveCollapsed &&
        Object.entries(deliveryOptions).map(([key, options]) => (
          <div key={key} className="delivery-methods__category">
            <label className="delivery-methods__category-label">
              {typeof GLOBAL_CONSTANTS.CATEGORY_NAMES[key] === "string"
                ? GLOBAL_CONSTANTS.CATEGORY_NAMES[key]
                : String(key)}
            </label>
            <Dropdown
              value={typeof deliveryMethod[key] === "string" ? deliveryMethod[key] : ""}
              onChange={(e) => handleDeliveryChange(key, e.target.value)}
              align="left"
              maxWidth="100%"
              className="delivery-methods__category-dropdown"
            >
              <option key="optimal" value="Оптимальный маршрут">
                Оптимальный маршрут
              </option>
              {options &&
                typeof options === "object" &&
                !Array.isArray(options) &&
                Object.keys(options).map((optionKey) => (
                  <option key={optionKey} value={optionKey}>
                    {optionKey}
                  </option>
                ))}
            </Dropdown>
          </div>
        ))}
    </div>
  );
}

export default DeliveryMethods;
